import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks"
import {
    selectDashboardBaselinePatientData,
    selectDashboardCurrentPatientData,
    selectDashboardSelectedValueType,
    selectDataPoint,
    selectValueTypeAggregationStrategies
} from "../../../redux/ProfessionalDashboard"
import { useEffect, useRef, useState } from "react"
import {
    aggregateValues,
    CombinedValue,
    DateAPI,
    GetPatientDataBaselineResponsePayload,
    GetPatientDataCurrentResponsePayload,
    MathAPI,
    Value,
    ValueType,
    ValueTypeAggregationStrategies,
    ValueTypeValueConverters,
    ValueTypeValueRequiresTimeDisplay
} from "pokal_api"
import { valueTypeNames } from "../../../util/valueTypeNames"
import notNull from "../../../util/notNull"
import { BarChart, EventProps } from "@tremor/react"
import SimpleDataView from "./SimpleDataView"
import {
    mockPatientUsed,
    mockPhqValueTypeUsed,
    mockSleepValueTypeUsed,
    selectOnboardingIsRunningState,
    selectOnboardingStep
} from "../../../redux/Onboarding"
import {
    MOCK_PHQ_BASELINE_DATA,
    MOCK_PHQ_CURRENT_DATA,
    MOCK_SLEEP_BASELINE_DATA,
    MOCK_SLEEP_CURRENT_DATA
} from "../../../onboarding/mock/mockPatientData"

interface CurrentPassiveDataViewProps {
    dataType: "current" | "baseline"
}

function AggregatedDataView(props: CurrentPassiveDataViewProps) {
    const strategies = useAppSelector(selectValueTypeAggregationStrategies)
    const step = useAppSelector(selectOnboardingStep)
    const running = useAppSelector(selectOnboardingIsRunningState)
    const dispatch = useAppDispatch()
    // index date string -> that day's data point (carries the per-day note)
    const dayMapRef = useRef<Record<string, Value>>({})

    const fetchedSelectedValueType = useAppSelector(
        selectDashboardSelectedValueType
    )
    const fetchedCurrentData = useAppSelector(selectDashboardCurrentPatientData)
    const fetchedBaselineData = useAppSelector(
        selectDashboardBaselinePatientData
    )

    const [selectedValueType, setSelectedValueType] =
        useState<ValueType | null>(null)
    const [currentData, setCurrentData] =
        useState<GetPatientDataCurrentResponsePayload | null>(null)
    const [baselineData, setBaslineData] =
        useState<GetPatientDataBaselineResponsePayload | null>(null)

    useEffect(() => {
        if (mockPatientUsed(running, step)) {
            if (mockPhqValueTypeUsed(running, step)) {
                setSelectedValueType(ValueType.PHQUESTIONNAIRE)
                setCurrentData(MOCK_PHQ_CURRENT_DATA)
                setBaslineData(MOCK_PHQ_BASELINE_DATA)
            } else if (mockSleepValueTypeUsed(running, step)) {
                setSelectedValueType(ValueType.SLEEP_DURATION)
                setCurrentData(MOCK_SLEEP_CURRENT_DATA)
                setBaslineData(MOCK_SLEEP_BASELINE_DATA)
            }
        } else {
            setSelectedValueType(fetchedSelectedValueType)
            setCurrentData(fetchedCurrentData)
            setBaslineData(fetchedBaselineData)
        }
    }, [
        fetchedBaselineData,
        fetchedCurrentData,
        fetchedSelectedValueType,
        step,
        running
    ])

    const [strategy, setStrategy] =
        useState<ValueTypeAggregationStrategies | null>(
            selectedValueType === null ? null : strategies[selectedValueType]
        )

    useEffect(() => {
        if (notNull(selectedValueType))
            setStrategy(strategies[selectedValueType])
    }, [strategies, selectedValueType])

    /* eslint-disable  @typescript-eslint/no-explicit-any */ //This is required here because of the charts.
    const [aggregatedData, setAggregatedData] = useState<{
        data: any[]
        names: string[]
        hasNotes: boolean
    }>({
        data: [],
        names: [],
        hasNotes: false
    })

    function aggregate() {
        if (notNull(selectedValueType) && notNull(strategy)) {
            let parsed: Value[] = []
            if (props.dataType === "current" && notNull(currentData)) {
                parsed = currentData
            }
            if (props.dataType === "baseline" && notNull(baselineData)) {
                parsed = baselineData
            }
            const aggregated = aggregateValues(parsed, strategy)
            const results: any[] = []
            const keyNames: string[] = []
            const typeName: string = valueTypeNames[selectedValueType]
            // Build a day -> note lookup (notes are attached per day on the raw values).
            const noteByDay = new Map<string, string>()
            for (const d of parsed) {
                if (d.note && d.note.trim())
                    noteByDay.set(new Date(d.date).toISOString().slice(0, 10), d.note)
            }
            const dayMap: Record<string, Value> = {}
            if (strategy === ValueTypeAggregationStrategies.COMBINE) {
                const sorted = [...aggregated].sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime())
                for (const value of sorted as CombinedValue[]) {
                    const element: any = {}
                    for (let i = 0; i < value.values.length; ++i) {
                        const name = typeName + " - Phase " + (i + 1)
                        if (!keyNames.includes(name)) keyNames.push(name)
                        element[name] = value.values[i]
                    }
                    const isoDay = new Date(value.date).toISOString().slice(0, 10)
                    const baseDate = new Date(value.date).toLocaleDateString("de")
                    const displayDate = noteByDay.has(isoDay) ? baseDate + " •" : baseDate
                    element.date = displayDate
                    results.push(element)
                    dayMap[displayDate] = {
                        id: isoDay,
                        date: new Date(value.date),
                        value: value.values.reduce((a, b) => a + b, 0),
                        note: noteByDay.get(isoDay)
                    }
                }
            } else {
                const sorted = [...aggregated].sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime())
                for (const value of sorted as Value[]) {
                    const indexDate = DateAPI.timestampToString(
                        new Date(value.date).getTime(),
                        false // immer nur Datum anzeigen
                    )
                    const isoDay = new Date(value.date).toISOString().slice(0, 10)
                    const displayDate = noteByDay.has(isoDay) ? indexDate + " •" : indexDate
                    results.push({
                        date: displayDate,
                        [typeName]: value.value
                    })
                    dayMap[displayDate] = {
                        id: isoDay,
                        date: new Date(value.date),
                        value: value.value,
                        note: noteByDay.get(isoDay)
                    }
                }
                keyNames.push(typeName)
            }
            dayMapRef.current = dayMap
            setAggregatedData({
                names: keyNames,
                data: results,
                hasNotes: noteByDay.size > 0
            })
        }
    }

    useEffect(() => {
        aggregate()
    }, [strategy, props.dataType === "current" ? currentData : baselineData])

    // Clear any selected day when the metric changes.
    useEffect(() => {
        dispatch(selectDataPoint(null))
    }, [selectedValueType])

    return notNull(selectedValueType) ? (
        <>
            {strategy === ValueTypeAggregationStrategies.COMPLETE ? (
                <SimpleDataView dataType={props.dataType} />
            ) : (
                <>
                    {aggregatedData.hasNotes ? (
                        <p className="text-xs text-gray-500 mb-1">
                            • kennzeichnet Tage mit Notiz
                        </p>
                    ) : null}
                    <BarChart
                        stack={true}
                        data={aggregatedData.data}
                        categories={aggregatedData.names}
                        index={"date"}
                        valueFormatter={(val) =>
                            ValueTypeValueConverters[selectedValueType](
                                MathAPI.roundToOneDecimal(val)
                            )
                        }
                        onValueChange={(v: EventProps) =>
                            dispatch(
                                selectDataPoint(
                                    v ? (dayMapRef.current[String(v.date)] ?? null) : null
                                )
                            )
                        }
                    />
                </>
            )}
        </>
    ) : (
        <></>
    )
}

export default AggregatedDataView
